/**
 * The four approved blog templates — the single definition of which templates
 * are offered to clients, in what order, at what length, and which one is the
 * quiet default. This module is the one source of truth; nothing else may
 * hardcode the set (rulings R-QC-1 AMENDED, R-QC-7, R-QC-8).
 *
 * Identity is the UUID only. Slug and name are deliberately never consulted:
 * the catalog contains look-alike and test rows, so a secondary match key can
 * put an unapproved record on a client surface.
 *
 * Order is fixed: the quiet default first, then descending real production
 * usage. It is not alphabetical and not a preference.
 *
 * Lengths are the templates' own targets. This file is the only place they are
 * stated for display; nothing derives a word count from client input.
 */

import type { BlogTemplateSummary } from '@/services/blogTemplatesService';

export interface ApprovedBlogTemplate {
  /** Stable identity and the only key used to resolve a template. */
  readonly id: string;
  /** Client-visible name. */
  readonly name: string;
  /** Target length in words, shown as an approximate figure. */
  readonly targetWords: number;
}

/** The four, in client-facing order. Index 0 is the quiet default. */
export const APPROVED_BLOG_TEMPLATES: readonly ApprovedBlogTemplate[] = [
  {
    id: '9510d85e-3d0c-4c51-bde4-133e5a4f6a26',
    name: 'Default SEO Blog',
    targetWords: 1500,
  },
  {
    id: '2f88c3f2-e783-486d-9bce-3433caee7020',
    name: 'Standard Informational',
    targetWords: 1500,
  },
  {
    id: 'd76e1335-01f0-4ac0-8523-d987d41173b2',
    name: 'How-To Guide',
    targetWords: 2000,
  },
  {
    id: 'd305883d-7030-4d0e-86b3-304e9e3b2826',
    name: 'Listicle',
    targetWords: 1200,
  },
] as const;

/** The template pre-selected when nothing else has been chosen. */
export const APPROVED_TEMPLATE_DEFAULT_ID = APPROVED_BLOG_TEMPLATES[0].id;

/** Count offered to clients. Reads better than a bare 4 at call sites. */
export const APPROVED_TEMPLATE_COUNT = APPROVED_BLOG_TEMPLATES.length;

/** A resolved catalog record. Only one of the four approved UUIDs can qualify. */
export interface ResolvedApprovedTemplate extends BlogTemplateSummary {
  isSubstitute: false;
}

/** Why an approved slot could not be offered. Callers may log these. */
export type DroppedApprovedReason = 'gate_conflict' | 'missing_from_catalog';

export interface ApprovedTemplateResolution {
  /** Ordered, ready to render. Contains only the four approved UUIDs. */
  templates: ResolvedApprovedTemplate[];
  /**
   * The quiet default (R-QC-8) — selection state only, no endorsement chrome.
   * Null only when nothing resolved (caller shows the empty state).
   */
  preselectedTemplateId: string | null;
  /** Approved ids that could not be offered, with the reason. */
  dropped: Array<{ id: string; reason: DroppedApprovedReason }>;
}

export interface ResolveApprovedTemplatesOptions {
  /**
   * Injected pre-selection (R-QC-7 seam). Honoured ONLY when the id resolved
   * into the offered set; otherwise the quiet first-slot default stands. This
   * is what makes an off-allowlist suggestion safe: a keyword-aware recommender
   * (or the advisory suggestion banner) can pass its chosen id here, and if
   * that id is not one of the approved four it is silently ignored rather than
   * surfaced — verified necessary against prod, where the suggestion pool holds
   * unapproved Comparison templates a comparison keyword would otherwise pick.
   */
  preselectedTemplateId?: string | null;
}

/**
 * Resolves the approved order against a live catalog and computes the
 * pre-selected id.
 *
 * Rules, in order:
 *  1. Locate each approved slot by UUID only.
 *  2. A slot whose record has gate_conflict is dropped, never shown with a
 *     warning — this surface does not advertise conflicts.
 *  3. A slot that locates nothing is dropped (no phantom cards).
 *  4. Pre-selection: the injected id when it survived into the offered set,
 *     else the first offered template, else null.
 */
export const resolveApprovedTemplates = (
  catalog: readonly BlogTemplateSummary[],
  options: ResolveApprovedTemplatesOptions = {},
): ApprovedTemplateResolution => {
  const byId = new Map(catalog.map((template) => [template.id, template]));

  const templates: ResolvedApprovedTemplate[] = [];
  const dropped: ApprovedTemplateResolution['dropped'] = [];

  for (const approved of APPROVED_BLOG_TEMPLATES) {
    const record = byId.get(approved.id);
    if (record === undefined) {
      dropped.push({ id: approved.id, reason: 'missing_from_catalog' });
      continue;
    }
    if (record.gate_conflict === true) {
      dropped.push({ id: approved.id, reason: 'gate_conflict' });
      continue;
    }
    templates.push({ ...record, isSubstitute: false });
  }

  const injected = options.preselectedTemplateId ?? null;
  const injectedSurvived = injected !== null && templates.some((t) => t.id === injected);
  const preselectedTemplateId = injectedSurvived
    ? injected
    : (templates[0]?.id ?? null);

  return { templates, preselectedTemplateId, dropped };
};
